import {
  GOOGLE_MAPS_API_KEY,
  googleMapThemeOptions,
} from '@features/r8tr/map/google-map/googleMapTheme';
import { useAppSelector } from '@lib/store/hooks';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import GoogleMapReact from 'google-map-react';

const DEFAULT_ZOOM = 15;
// Shown only until an address-only job is geocoded
const FALLBACK_CENTER = { lat: 39.8283, lng: -98.5795 };

interface JobLocationMapProps {
  latitude?: number | null;
  longitude?: number | null;
  address?: string | null;
  height?: number;
  sx?: SxProps<Theme>;
}

export const JobLocationMap = ({
  latitude,
  longitude,
  address,
  height = 240,
  sx,
}: JobLocationMapProps): JSX.Element => {
  const mode = useAppSelector((state) => state.colorMode.mode);
  const hasCoords = latitude != null && longitude != null;
  const center = hasCoords
    ? { lat: Number(latitude), lng: Number(longitude) }
    : FALLBACK_CENTER;

  return (
    <Box
      sx={[
        { height, width: '100%', bgcolor: 'rgb(var(--card))' },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <GoogleMapReact
        key={`${mode}:${center.lat},${center.lng}:${address ?? ''}`}
        bootstrapURLKeys={{ key: GOOGLE_MAPS_API_KEY }}
        defaultCenter={center}
        defaultZoom={DEFAULT_ZOOM}
        options={(maps) => ({
          ...googleMapThemeOptions(mode, maps),
          disableDefaultUI: true,
          zoomControl: true,
        })}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map, maps }) => {
          if (hasCoords) {
            new maps.Marker({ position: center, map });
            return;
          }
          if (!address) {
            return;
          }
          new maps.Geocoder().geocode({ address }, (results, status) => {
            const location = results?.[0]?.geometry.location;
            if (status !== 'OK' || !location) {
              return;
            }
            const position = { lat: location.lat(), lng: location.lng() };
            map.setCenter(position);
            new maps.Marker({ position, map });
          });
        }}
      />
    </Box>
  );
};
